import { useState, useEffect } from 'react';
import { Play, Tv, CheckCircle } from 'lucide-react';
import { ContentRail } from './ContentRail';
import { formatDuration } from '../../utils/homeHelpers';
import { getTeamFlag } from '../../utils/teamFlags';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function TopMatchesSection({ navigate, onPlayVideo }) {
  const [matches, setMatches] = useState([]);

  useEffect(() => {
    fetch(`${API}/content/top-matches?limit=15`)
      .then(r => r.json())
      .then(data => setMatches(data.matches || []))
      .catch(() => {});
  }, []);

  if (!matches.length) return null;

  return (
    <ContentRail
      icon={<Tv className="w-5 h-5 text-emerald-500 shrink-0" />}
      title="Top African Matches"
      subtitle="The biggest games on the continent"
      seeAllPath="/browse"
      navigate={navigate}
      testId="top-matches-section"
    >
      {matches.map((item) => {
        const teamsStr = item.teams || item.title || '';
        const parts = teamsStr.split(/ vs /i);
        const home = parts[0]?.trim() || '';
        const away = parts[1]?.trim() || '';
        const homeFlag = getTeamFlag(home);
        const awayFlag = getTeamFlag(away);

        return (
          <div
            key={item.id}
            onClick={() => {
              if (item.source === 'youtube' && item.youtube_video_id) onPlayVideo(item);
              else navigate(`/match/${item.id}`);
            }}
            className="flex-shrink-0 w-[260px] sm:w-[300px] cursor-pointer group snap-start"
            data-testid={`top-match-card-${item.id}`}
          >
            <div className="relative aspect-video rounded-xl overflow-hidden mb-2.5">
              <img src={item.thumbnail_url} alt={teamsStr} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
              {item.is_live && (
                <div className="absolute top-2 left-2 px-1.5 py-0.5 bg-red-600 text-white text-[9px] font-bold rounded animate-pulse">LIVE</div>
              )}
              {!item.is_live && item.competition_name && (
                <div className="absolute top-2 left-2 px-1.5 py-0.5 bg-black/60 backdrop-blur-sm border border-white/10 rounded-full">
                  <span className="text-emerald-400 text-[8px] font-bold uppercase tracking-wider">{item.competition_name}</span>
                </div>
              )}
              {item.has_real_data && (
                <div className="absolute top-2 right-2 flex items-center gap-1 px-1.5 py-0.5 bg-emerald-500/15 backdrop-blur-sm border border-emerald-500/25 rounded-full" data-testid={`verified-top-${item.id}`}>
                  <CheckCircle className="w-2.5 h-2.5 text-emerald-400" />
                  <span className="text-emerald-400 text-[8px] font-bold">Verified</span>
                </div>
              )}
              {item.duration && (
                <div className="absolute bottom-2 right-2 px-1.5 py-0.5 bg-black/80 text-white text-[9px] font-medium rounded">{formatDuration(item.duration)}</div>
              )}
              {/* Team flags overlay */}
              {homeFlag && awayFlag && (
                <div className="absolute bottom-2 left-2 flex items-center gap-1 px-1.5 py-0.5 bg-black/60 backdrop-blur-sm rounded-full border border-white/10">
                  <span className="text-sm">{homeFlag}</span>
                  <span className="text-white/40 text-[8px] font-bold">vs</span>
                  <span className="text-sm">{awayFlag}</span>
                </div>
              )}
              <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <div className="w-11 h-11 bg-emerald-600 rounded-full flex items-center justify-center shadow-lg shadow-emerald-600/30">
                  <Play className="w-4 h-4 text-white" fill="currentColor" />
                </div>
              </div>
            </div>
            <h3 className="text-white font-semibold text-sm group-hover:text-emerald-400 transition-colors line-clamp-2">
              {home && away ? `${home} vs ${away}` : teamsStr}
            </h3>
            <p className="text-gray-500 text-xs mt-0.5 line-clamp-1">
              {item.is_live ? 'Live now' : item.duration ? formatDuration(item.duration) : ''}
              {item.competition_name ? ` \u00B7 ${item.competition_name}` : ''}
            </p>
          </div>
        );
      })}
    </ContentRail>
  );
}
